import { useState } from "react";
import type { FeatureFlags } from "../services/api";
import { updateFeatureFlags } from "../services/api";
import { useFeatureFlags } from "../contexts/FeatureFlagsContext";
import { getErrorAlertMessage } from "../utils/errors";

export function FeatureFlagsPanel() {
  const { flags, refresh } = useFeatureFlags();
  const [localFlags, setLocalFlags] = useState<FeatureFlags>(flags);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleToggle = (key: keyof FeatureFlags) => {
    setLocalFlags((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      await updateFeatureFlags(localFlags);
      await refresh();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(getErrorAlertMessage("update feature flags", err));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setLocalFlags(flags);
    setError(null);
    setSuccess(false);
  };

  const featureSections = [
    {
      title: "Top-Level Routes",
      features: [
        { key: "dashboard" as const, label: "Dashboard (Admin/Ops)" },
        { key: "myDashboard" as const, label: "My Dashboard (Consultant)" },
        { key: "jobBoard" as const, label: "Job Board" },
        { key: "reports" as const, label: "Reports" },
        { key: "admin" as const, label: "Admin" },
      ],
    },
    {
      title: "Dashboard Tabs",
      features: [
        { key: "people" as const, label: "CoE Pool" },
        { key: "beach" as const, label: "Beach" },
        { key: "awsRrs" as const, label: "AWS RR's" },
        { key: "skills" as const, label: "Skills Catalog" },
        { key: "assignments" as const, label: "Assignments" },
        { key: "opportunities" as const, label: "Opportunities" },
        { key: "rrMatcher" as const, label: "RR Matcher" },
      ],
    },
  ];

  return (
    <div className="layout single-column">
      <div className="panel">
        <div className="panel-header">
          <h2>Feature Flags</h2>
          <div style={{ display: "flex", gap: "0.75rem" }}>
            <button type="button" className="ghost" onClick={handleReset} disabled={saving}>
              Reset
            </button>
            <button type="button" className="primary" onClick={() => void handleSave()} disabled={saving}>
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>

        {error && <div className="alert error">{error}</div>}
        {success && <div className="alert info">Feature flags updated successfully!</div>}

        <div style={{ display: "grid", gap: "2rem" }}>
          {featureSections.map((section) => (
            <div key={section.title}>
              <h3 style={{ marginBottom: "1rem", fontSize: "1.1rem" }}>{section.title}</h3>
              <div style={{ display: "grid", gap: "0.75rem" }}>
                {section.features.map((feature) => (
                  <label
                    key={feature.key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      padding: "0.75rem",
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                      cursor: "pointer",
                      transition: "background-color 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#f8fafc";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={localFlags[feature.key]}
                      onChange={() => handleToggle(feature.key)}
                      style={{ width: "20px", height: "20px", cursor: "pointer" }}
                    />
                    <span style={{ fontWeight: "500" }}>{feature.label}</span>
                    <span
                      style={{
                        marginLeft: "auto",
                        padding: "0.25rem 0.5rem",
                        borderRadius: "999px",
                        fontSize: "0.75rem",
                        fontWeight: "600",
                        backgroundColor: localFlags[feature.key] ? "#d1fae5" : "#fee2e2",
                        color: localFlags[feature.key] ? "#065f46" : "#991b1b",
                      }}
                    >
                      {localFlags[feature.key] ? "ENABLED" : "DISABLED"}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: "1.5rem", padding: "1rem", backgroundColor: "#fef3c7", borderRadius: "8px", border: "1px solid #fbbf24" }}>
          <strong style={{ color: "#92400e" }}>⚠️ Warning:</strong>
          <p style={{ margin: "0.5rem 0 0", color: "#92400e", fontSize: "0.9rem" }}>
            Disabling features will immediately affect all users. Users may need to refresh their browser to see changes.
            Be cautious when disabling features that are currently in use.
          </p>
        </div>
      </div>
    </div>
  );
}
